<script setup>
import { onMounted, ref, nextTick, watch, computed } from 'vue';
import { Notification } from 'Js/signalR/notification.js';
import EnTab from 'Components/tabs/EnTab/EnTab.vue';
import EnButton from 'Components/base/EnButton/EnButton.vue';
import EnTabView from 'Components/tabs/EnTabView/EnTabView.vue';
import NotificationPanelTab from 'Components/notifications/EnNotificationPanel/NotificationPanelTab.vue';
import NotificationMessage from './NotificationMessage.vue';
import EnIcon from 'Components/icons/EnIcon.vue';
import EnSkeleton from 'Components/base/EnSkeleton/EnSkeleton.vue';
import { useI18n } from 'vue-i18n';
import { EnOverlayBadge } from 'Components/badge/EnOverlayBadge';

const {t} = useI18n();
const refs = ref([]);
const panel = ref();

// Height of the top navigation bar in pixels
const navbarHeight = 72;
// Height of the top panel, button, and tab header combined in pixels
const tabHeaderHeight = 155;
// Height of each skeleton item (card) including margins in pixels
const skeletonItemHeight = 100;

/**
 * Calculate the number of skeleton loaders to display based on the panel height.
 */
const skeletonCount = computed(() => {
  const panelHeight = window.innerHeight - navbarHeight;
  return Math.ceil((panelHeight - tabHeaderHeight) / skeletonItemHeight);
});
const notificationToggler = ref(null);
const messages = ref([]);
// const unreadMessages = ref([]);
const unreadMessagesCount = ref(0);
const incomeMessages = ref([]);
const isPanelShown = ref(false);
const loading = ref(false);
let subscribed = false;
let page = 0;
let lastReloadPage = null;
let allItemsLoaded = false;
let currentIsRead = false;


function renderTab(val) {
  if (!refs.value.includes(val))
    refs.value.push(val);
}

const pushMessage = (message) => {
  incomeMessages.value.unshift(message);
  messages.value.unshift(message);

  setTimeout(() => {
    incomeMessages.value = incomeMessages.value.filter(m => m.id !== message.id);
  }, 5000);
  getNotificationList();
};
// window.globalNotifier = pushMessage;

const toggleVisibilityPanel = async () => {
  isPanelShown.value = !isPanelShown.value;
  if (isPanelShown.value) {
    await getNotificationList();
  }
};

async function subscribe() {
  await nextTick();
  const menuContent = document.querySelector('.notification-list-panel');

  if (menuContent) {
    menuContent.addEventListener('scroll', detectScrollEnd);
  }
}

function detectScrollEnd(val) {
  if (!val.target) {
    return;
  }
  // Track for end of scrollbar
  if (val.target.scrollHeight - val.target.scrollTop <= val.target.clientHeight + 1 && !allItemsLoaded) {
    // Load additional 10 items
    page++;
    getNotificationList(currentIsRead);
  }
}

async function tabAllNotificationsSelected(isRead) {
  page = 0;
  allItemsLoaded = false;
  currentIsRead = isRead;

  await getNotificationList(isRead);

  await subscribe();
  subscribed = true;
}

async function markNotificationAsRead(message, rerender = false) {
  await fetch('/api/Notifications/UpdateNotificationRead?timestamp=' + encodeURIComponent(message.timestamp), {method: 'POST'});
  await getNotificationList();
  return message;
}

async function markAllAsRead() {
  for (let message of messages.value) {
    if (message.readAt === null) {
      await markNotificationAsRead(message);
    }
  }
  await getNotificationList();
}

async function getNotificationList(isRead = false) {
  return;
  loading.value = true;

  if (page === 0) {
    messages.value = [];
  }

  lastReloadPage = page;
  const response = await fetch(`/api/Notifications/GetNotifications?${new URLSearchParams({
    isRead: isRead,
    skip: page * 10,
    take: 10,
  })}`);

  if (response.ok) {
    const responseData = await response.json();
    const resItems = responseData.Notifications.map(notification => Notification.from(notification));
    if (page !== 0) {
      messages.value.push(...resItems);
      // 0 items means only that we reached last available page
      if (resItems.length === 0) {
        allItemsLoaded = true;
      }
    } else {
      messages.value = resItems;
    }
    if (!isRead) {
      unreadMessagesCount.value = responseData.TotalCount;
    }
  }
  loading.value = false;
}

onMounted(async () => {
  const replacableContent = document.getElementById('header-notification');
  if (replacableContent) {
    replacableContent.replaceWith(notificationToggler.value);
  }
});


</script>

<template>
  <div>
    <div class="notification-panel-toggle-button mr-5" ref="notificationToggler">
      <en-button static-id="button-show-notification-panel" @click="toggleVisibilityPanel">
        <span class="relative inline-flex">
          <en-icon color="background">mdi-bell-outline</en-icon>
          <en-overlay-badge :count="unreadMessagesCount" variant="destructive" class="z-2"></en-overlay-badge>
        </span>
      </en-button>
    </div>

    <div class="notification-center-wrapper">
      <div class="notification-center">
        <transition-group name="fade">
          <template v-for="message in incomeMessages" :key="message.timestamp">
            <transition name="scroll-y-reverse">
              <notification-message :message="message" is-panel></notification-message>
            </transition>
          </template>
        </transition-group>
      </div>
    </div>

    <div class="notification-list-panel-wrapper" ref="panel">
      <div class="overlay" @click="toggleVisibilityPanel" :class="{'show': isPanelShown}"></div>
      <div class="notification-list-panel shadow-lg" :class="{'show': isPanelShown}">
        <div class="top-panel d-flex justify-end pa-1">
          <en-button
              static-id="mark-all-as-read-button"
              @click="markAllAsRead"
          >
            {{ t('notifications.markAsRead') }}
          </en-button>
        </div>
        <en-tab-view bold :refs="refs">
          <en-tab
              :title="t('notifications.new')"
              @has-rendered="tabAllNotificationsSelected"
              only-render-when-active
              :ref="(el) => { renderTab(el) }">
            <div>
              <template v-if="loading && page === 0">
                <div class="skeleton-container">
                  <en-skeleton
                      v-for="n in skeletonCount"
                      :key="n"
                      class="mx-5 my-3"
                      height="76px"
                  ></en-skeleton>
                </div>
              </template>
              <notification-panel-tab
                  v-else
                  :messages="messages"
                  @message-close="(message) => markNotificationAsRead(message, true)"
              ></notification-panel-tab>
              <en-skeleton
                  v-if="loading && page > 0"
                  class="mx-5 my-3"
                  height="76px"
              ></en-skeleton>
            </div>
          </en-tab>
          <en-tab
              :title="t('notifications.all')"
              only-render-when-active
              :ref="(el) => { renderTab(el) }"
              @has-rendered="() => tabAllNotificationsSelected(true)">
            <div>
              <template v-if="loading && page === 0">
                <div class="skeleton-container">
                  <en-skeleton
                      v-for="n in skeletonCount"
                      :key="n"
                      class="mx-5 my-3"
                      height="76px"
                  ></en-skeleton>
                </div>
              </template>
              <notification-panel-tab
                  v-else
                  :messages="messages"
                  :can-be-closed="false"
              ></notification-panel-tab>
              <en-skeleton
                  v-if="loading && page > 0"
                  class="mx-5 my-3"
                  height="76px"
              ></en-skeleton>
            </div>
          </en-tab>
        </en-tab-view>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import "Styles/helper/variables.scss";

.scroll-y-reverse-enter-active, .scroll-y-reverse-leave-active {
  transition: transform .2s cubic-bezier(.25, .8, .5, 1), opacity .2s cubic-bezier(.25, .8, .5, 1);
}

.scroll-y-reverse-enter-from, .scroll-y-reverse-leave-to {
  opacity: 0;
  transform: translateY(15px);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.notification-center-wrapper {
  top: 0;

  .notification-center {
    position: fixed;
    top: 92px;
    right: 20px;
    width: 400px;
    z-index: 4;

    .v-alert {
      overflow: visible;
      margin: 10px 20px;

    }
  }
}

.notification-list-panel-wrapper {
  top: 0;

  .top-panel {
    & > div {
      margin: -25px 10px 5px 0;
    }
  }

  .tab-bar .en-tab-placeholder {
    border-bottom: 3px solid #ddd;
  }

  .tabs-header {
    padding: 0 1px;

    .en-tab-header {
      background: $en-0;
      text-align: center;
      border-bottom: 3px solid #ddd;
      color: #9d9d9d;

      &.selected {
        border-color: #0c3148;
        color: #0c3148;
      }
    }
  }


  .overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 0;
    height: 0;

    &.show {
      width: calc(100vw - 72px);
      height: 100vh;
    }
  }


  .notification-list-panel {
    transition: ease 0.5s;
    right: -500px;
    width: 500px;
    background-color: $en-0;
    height: calc(100vh - 72px);
    position: fixed;
    top: 72px;
    z-index: 500;
    padding-top: 30px;
    overflow: auto;
    border-top: 1px solid $en-0;

    .en-button-wrapper {
      border-radius: 14px;

      button {
        border-radius: 14px;
      }
    }


    .messages-placeholder-wrapper {
      display: flex;
      width: 100%;
      height: calc(100vh - 72px - 60px - 155px); // Navbar, TopMenu with button and en-tab component header

      .messages-placeholder {
        margin: auto;
      }

    }

    &.show {
      right: 0
    }

    .v-alert {
      overflow: visible;
      margin: 10px 20px;
    }
  }
}
</style>